// URLs that came from somebody else, made safe to put in an href or a src.
//
// Most links on this site are typed by a person who is not the reader: a club
// writing an event, an editor writing a tour stop or a Welcome Day resource, a
// student whose report records the page they were on, a parker attaching a
// photo ID. Rendering whatever they typed straight into an <a href> is how a
// `javascript:` link runs in the reader's browser, and how a link that looks
// like ours sends a moderator to somebody else's login page.
//
// React 19 already refuses a `javascript:` href, and the site's CSP refuses
// inline script, so this is the third fence rather than the only one — which
// is why it is deliberately boring: absolute http(s), a path on this site, or a
// mail/phone link. Anything else becomes '' and every caller treats '' as "no
// link", the same way it treats a link nobody filled in.
//
// Pure and DOM-free (the origin is passed in), so `node src/utils/safeUrl.test.mjs`
// can pin the refusals.

// Written as a loop rather than a /[\x00-\x1f]/ class: CRA's lint config warns
// on control characters in a regex, and on Vercel a warning fails the build.
function hasControl(s) {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return true;
  }
  return false;
}

/**
 * An href a stored link may use, or '' when it may not.
 *
 * Accepts `https://…`, `http://…`, a site path (`/umanitoba?b=…`), a fragment
 * or query on this page, and `mailto:` / `tel:`. Refuses every other scheme
 * (`javascript:`, `data:`, `vbscript:`, `blob:`, `file:`…) and a
 * protocol-relative `//host`, which names another site while looking like one
 * of our paths.
 */
export function safeHref(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s || s.length > 2000) return '';
  // A control character inside a scheme is how `java\tscript:` gets past a
  // naive prefix check; nothing legitimate carries one.
  if (hasControl(s)) return '';
  if (s.startsWith('//') || s.startsWith('/\\')) return '';
  if (s.startsWith('/') || s.startsWith('#') || s.startsWith('?')) return s;
  if (/^https?:\/\/[^/\\\s]/i.test(s)) return s;
  if (/^mailto:[^\s]+$/i.test(s) || /^tel:[+\d][\d\s().-]*$/i.test(s)) return s;
  return '';
}

/**
 * The URL only if it is a page on one of `origins` (this site), else ''.
 *
 * For a URL that should only ever point back at us — a student's report
 * records the page they were on — so a report posted straight to the API with
 * somebody else's address in it cannot put a link to that address in front of
 * a volunteer under the label "Page they were on".
 */
export function ownSiteUrl(raw, origins = []) {
  const s = safeHref(raw);
  if (!/^https?:\/\//i.test(s)) return '';
  let u;
  try { u = new URL(s); } catch { return ''; }
  const allowed = new Set(origins.filter(Boolean).map(o => String(o).toLowerCase()));
  return allowed.has(u.origin.toLowerCase()) ? u.href : '';
}

/** The production origins, for `ownSiteUrl` — the site answers on both. */
export const SITE_ORIGINS = ['https://myumaps.ca', 'https://www.myumaps.ca'];

/**
 * A stored document (a ParkConnect photo ID or receipt) that is safe to show
 * inline in an <iframe>/<img>: a file in this deployment's Vercel Blob store,
 * or the data: URI the local dev server inlines. '' for anything else.
 *
 * Only an upload made through our own endpoint ever produces one of these; a
 * submission posted straight to the API can carry any URL, and an arbitrary
 * page framed inside the moderation screen is a phishing form that looks like
 * part of it.
 */
export function storedDocUrl(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s || hasControl(s)) return '';
  if (/^data:(image\/(png|jpe?g|webp|heic)|application\/pdf);base64,[A-Za-z0-9+/=]+$/i.test(s)) return s;
  let u;
  try { u = new URL(s); } catch { return ''; }
  if (u.protocol !== 'https:') return '';
  return /\.blob\.vercel-storage\.com$/i.test(u.hostname) ? u.href : '';
}
